import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MEDIA_QK } from "@/services/mediaLibrary";
import { marketingApi, MKT_QK } from "./api";

/**
 * MKT-1 · 图片库「实拍授权」三态（订阅版才出；企业版图片库零变化）——产品契约 V1.1 §A1 licensed。
 * · 是 = 实拍或已取得授权，可以对外公开发布；否 = 明确不可对外（只作内部参考）；未声明 = 未知（默认）
 * · 只有「是」能被 S4 拿去发帖；改动写操作日志，并刷新引用它的产品对外 updatedAt（后端做）
 * 接口：PATCH /api/v1/media-library/{id}/licensed · POST /api/v1/media-library:set-licensed
 */
export type Licensed = boolean | null;

export const LICENSED_LABEL = (v: Licensed | undefined) => (v === true ? "已授权" : v === false ? "不可对外" : "未声明授权");
const OPTIONS: { v: Licensed; label: string }[] = [{ v: true, label: "是" }, { v: false, label: "否" }, { v: null, label: "未声明" }];

/** 图片卡片上的小标签。 */
export function LicensedTag({ value }: { value: Licensed | undefined }) {
  const tone = value === true ? "bg-emerald-50 text-emerald-700" : value === false ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700";
  return <span className={`mt-0.5 self-start rounded px-1.5 py-0.5 text-[11px] ${tone}`} data-testid="licensed-tag">{LICENSED_LABEL(value)}</span>;
}

/** 筛选下拉（图片库筛选行）。值：""（全部）/ yes / no / undeclared，原样作为 ?licensed= 传给后端。 */
export function LicensedFilter({ value, onChange, className }: { value: string; onChange: (v: string) => void; className: string }) {
  return (
    <label className="flex items-center gap-1.5 text-[13px] text-slate-500">实拍授权：
      <select aria-label="实拍授权" className={className} value={value} onChange={(e) => onChange(e.target.value)} data-testid="licensed-filter">
        <option value="">全部</option><option value="yes">是</option><option value="no">否</option><option value="undeclared">未声明</option>
      </select>
    </label>
  );
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: MEDIA_QK.libraryAll });
    qc.invalidateQueries({ queryKey: MKT_QK.products });
  };
}

/** 抽屉里单张标注：是 / 否 / 未声明。 */
export function LicensedPicker({ assetId, value, canEdit }: { assetId: string; value: Licensed | undefined; canEdit: boolean }) {
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: (v: Licensed) => marketingApi.setLicensed(assetId, v),
    onSuccess: () => { setErr(null); invalidate(); qc.invalidateQueries({ queryKey: MEDIA_QK.asset(assetId) }); },
    onError: (e) => setErr(e instanceof Error ? e.message : "保存失败，请重试"),
  });
  const cur = value === undefined ? null : value;
  return (
    <div className="rounded-lg border border-slate-200 p-2.5" data-testid="licensed-picker">
      <div className="flex items-center gap-2 text-[13px]">
        <span className="font-semibold text-slate-800">实拍授权</span>
        <span className="flex-1" />
        {OPTIONS.map((o) => (
          <button key={String(o.v)} type="button" aria-pressed={cur === o.v} disabled={!canEdit || mut.isPending} onClick={() => mut.mutate(o.v)}
            data-testid={`licensed-${o.v === null ? "null" : o.v ? "yes" : "no"}`}
            className={`rounded-full border px-2.5 py-0.5 text-[12px] disabled:opacity-60 ${cur === o.v ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-600 hover:border-brand"}`}>
            {o.label}
          </button>
        ))}
      </div>
      <p className="mt-1.5 text-[11.5px] text-slate-400">是 = 实拍或已取得授权，可对外发布；否 = 只作内部参考。只有「是」的图会被社媒（S4）拿去发帖。</p>
      {err && <p className="mt-1 text-[12px] text-rose-600" role="alert">{err}</p>}
    </div>
  );
}

/** 批量：把当前筛选出来的图（最多 500 张）一起标成是 / 否 / 未声明，先确认。 */
export function LicensedBatchBar({ assetIds, total }: { assetIds: string[]; total: number }) {
  const invalidate = useInvalidate();
  const [pending, setPending] = useState<Licensed | undefined>(undefined);
  const [msg, setMsg] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: (v: Licensed) => marketingApi.setLicensedBatch(assetIds.slice(0, 500), v),
    onSuccess: (r) => { setPending(undefined); setMsg(`已改 ${r.updated} 张${r.unchanged ? `（${r.unchanged} 张本来就是）` : ""}`); invalidate(); },
    onError: (e) => { setPending(undefined); setMsg(e instanceof Error ? e.message : "批量保存失败，请重试"); },
  });
  if (assetIds.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600" data-testid="licensed-batch">
      <span>把下面这 {Math.min(assetIds.length, 500)} 张{total > assetIds.length ? `（共 ${total} 张，只改显示出来的）` : ""}一起标为实拍授权：</span>
      {OPTIONS.map((o) => (
        <button key={String(o.v)} type="button" onClick={() => { setPending(o.v); setMsg(null); }} data-testid={`batch-licensed-${o.v === null ? "null" : o.v ? "yes" : "no"}`}
          className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 hover:border-brand hover:text-brand">{o.label}</button>
      ))}
      {pending !== undefined && (
        <span className="flex items-center gap-2" role="alertdialog">
          <span className="text-amber-800">确认把 {Math.min(assetIds.length, 500)} 张都标为「{OPTIONS.find((o) => o.v === pending)?.label}」？</span>
          <button type="button" data-testid="batch-licensed-ok" disabled={mut.isPending} onClick={() => mut.mutate(pending)} className="rounded-md bg-brand px-2.5 py-0.5 text-white disabled:opacity-50">确认</button>
          <button type="button" onClick={() => setPending(undefined)} className="rounded-md border border-slate-300 bg-white px-2.5 py-0.5">取消</button>
        </span>
      )}
      {msg && <span className="text-slate-500" data-testid="batch-licensed-msg">{msg}</span>}
    </div>
  );
}
